import { useState } from 'react';
import { ShieldAlert, Filter, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/providers/ToastProvider';
import { FraudAlertList } from '../components/FraudAlertList';
import { useAdminFraud, useAdminFraudStats, useReviewFraudAlert } from '../hooks/useAdminFraud';

export default function AdminFraudPage() {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [levelFilter, setLevelFilter] = useState<string>('ALL');

  const { data, isLoading } = useAdminFraud({
    page,
    limit: 10,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    level: levelFilter !== 'ALL' ? levelFilter : undefined,
  });

  const { data: stats } = useAdminFraudStats();
  const reviewMutation = useReviewFraudAlert();

  const handleReview = async (
    id: string,
    status: 'REVIEWED' | 'DISMISSED',
    notes?: string
  ) => {
    try {
      await reviewMutation.mutateAsync({ id, status, notes });
      toast({
        title: status === 'REVIEWED'
          ? 'Alerta aprovado e marcado como revisado'
          : 'Alerta descartado',
        variant: 'success'
      });
    } catch {
      toast({ title: 'Erro ao revisar alerta', variant: 'error' });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-6 w-6 text-destructive" />
          <h1 className="text-2xl font-bold">Alertas de Fraude</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-warning/10 rounded-lg">
                <Clock className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pendentes</p>
                <p className="text-2xl font-bold">{stats?.pendingAlerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-destructive/10 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Alto Risco</p>
                <p className="text-2xl font-bold">{stats?.highRiskAlerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-destructive/10 rounded-lg">
                <ShieldAlert className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Crítico</p>
                <p className="text-2xl font-bold">{stats?.criticalRiskAlerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-success/10 rounded-lg">
                <CheckCircle className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Descartados</p>
                <p className="text-2xl font-bold">{stats?.dismissedAlerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Status</SelectItem>
                <SelectItem value="PENDING">Pendente</SelectItem>
                <SelectItem value="REVIEWED">Revisado</SelectItem>
                <SelectItem value="DISMISSED">Descartado</SelectItem>
              </SelectContent>
            </Select>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Nível" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Níveis</SelectItem>
                <SelectItem value="LOW">Baixo</SelectItem>
                <SelectItem value="MEDIUM">Médio</SelectItem>
                <SelectItem value="HIGH">Alto</SelectItem>
                <SelectItem value="CRITICAL">Crítico</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <FraudAlertList
        alerts={data?.data || []}
        isLoading={isLoading}
        total={data?.meta.total || 0}
        page={page}
        pageSize={data?.meta.perPage || 10}
        onPageChange={setPage}
        onReview={handleReview}
        isReviewing={reviewMutation.isPending}
      />
    </div>
  );
}
